import classNames from 'classnames';
import { Stack } from '@components/layout';
import { Body } from '@components/text';
import './FooterDisclaimer.scss';

/**
 * Footer Disclaimer Props
 * @param text - disclaimer text; pass an array to render multiple lines (e.g. legal copy)
 * @param align - text alignment for the disclaimer lines (defaults to 'start')
 * @param className - optional additional class name
 *
 * Extends `React.HTMLAttributes<HTMLDivElement>` (minus the native `align`) so consumers can
 * pass through attributes like `id`, `data-testid`, and `aria-*` to the underlying element.
 */
interface FooterDisclaimerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'align' | 'children'> {
  text: string | string[];
  align?: 'start' | 'center';
}

/**
 * Footer Disclaimer, keyboard-focusable so screen readers announce it when tabbing
 * @param text - disclaimer text; pass an array to render multiple lines (e.g. legal copy)
 * @param align - text alignment for the disclaimer lines (defaults to 'start')
 * @param className - optional additional class name
 * @example
 * ```tsx
 * <FooterDisclaimer text="© 2023 Paramount. All Rights Reserved." />
 * <FooterDisclaimer text={['© 2023 Paramount. All Rights Reserved.', 'Conteúdo sujeito a alterações.']} />
 * ```
 */
export const FooterDisclaimer = ({
  text,
  align = 'start',
  className,
  ...rest
}: FooterDisclaimerProps) => {
  const lines = Array.isArray(text) ? text : [text];
  return (
    <Stack
      flexDirection="column"
      gap="spacing-1"
      className={classNames('vds-footer-disclaimer', className)}
      role="note"
      tabIndex={0}
      {...rest}>
      {lines.map((line, index) => (
        <Body key={index} size="small" variant="caption" color="tertiary" align={align}>
          {line}
        </Body>
      ))}
    </Stack>
  );
};
